import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { customRequirementsAtom } from './../data-access/atoms/custom-requirements.atom';
import { useRequirementStatuses } from './useRequirementStatuses';

export const useCustomRequirementsHandler = ({
	goBack,
	handleComplete,
	onRequirementsDenied
}: {
	goBack: () => void;
	handleComplete: () => void;
	onRequirementsDenied: () => void;
}) => {
	const [{ requirementIndex, requirements }, setCustomRequirement] = useAtom(customRequirementsAtom);
	const [showSummaryScreen, setShowSummaryScreen] = useState(true);
	const [shouldResetIndex, setShouldResetIndex] = useState(false);
	const {
		allRequirementsCompleted,
		currentRequirement,
		currentRequirementStatus,
		firstRetryRequirementIndex,
		loadingStatuses,
		refetchRequirementStatuses,
		requirementStatuses
	} = useRequirementStatuses();

	const onContinue = useCallback(() => {
		if (requirements && requirementIndex >= requirements.length) {
			goBack();
		} else {
			setShowSummaryScreen(false);
		}
	}, [requirements, requirementIndex, goBack]);

	const someRequirementIsDenied = useMemo(
		() =>
			requirementStatuses.length &&
			requirementStatuses.some(requirement => requirement.status === 'completed_deny'),
		[requirementStatuses]
	);

	useEffect(() => {
		if (loadingStatuses || !currentRequirementStatus) return;

		if (['completed', 'underReview'].includes(currentRequirementStatus)) {
			setCustomRequirement({ type: 'INCREMENT' });
		}
	}, [currentRequirementStatus, loadingStatuses, setCustomRequirement]);

	useEffect(() => {
		if (someRequirementIsDenied) {
			onRequirementsDenied();
		}
	}, [someRequirementIsDenied, onRequirementsDenied]);

	useEffect(() => {
		const handleEndOfList = async () => {
			if (!requirements || requirementIndex < requirements.length || showSummaryScreen) return;

			await refetchRequirementStatuses();

			setShouldResetIndex(true);
		};

		handleEndOfList();
	}, [requirements, requirementIndex, refetchRequirementStatuses, showSummaryScreen, setCustomRequirement]);

	useEffect(() => {
		if (shouldResetIndex) {
			if (firstRetryRequirementIndex !== -1) {
				setCustomRequirement({ payload: firstRetryRequirementIndex, type: 'SET_INDEX' });
			}
			setShouldResetIndex(false);
			setShowSummaryScreen(true);
		}
	}, [shouldResetIndex, firstRetryRequirementIndex, setCustomRequirement, setShouldResetIndex]);

	useEffect(() => {
		if (allRequirementsCompleted) {
			handleComplete();
		}
	}, [allRequirementsCompleted, handleComplete]);

	return {
		currentRequirement,
		loadingStatuses,
		onContinue,
		showSummaryScreen,
		someRequirementIsDenied
	};
};
